/**
 * The flashlight: ownership, charge and the toggle.
 *
 * The player starts a run without one. Finding it is meant to be an event, and
 * the beam is a resource rather than a permanent upgrade — the charge is a
 * timer that runs whenever the light is on and only batteries push it back up.
 *
 * The click is not played here. `flashlight:toggle` carries it, so the audio
 * engine decides what a switch sounds like.
 */

import { TUNING } from '../core/config';
import { clamp } from '../core/math';
import type { EventBus, PlayerState } from '../core/types';

/** Seconds between "you do not have a flashlight" nudges. */
const NAG_INTERVAL = 3;

/**
 * Charge in the flashlight when it is first found. It has been lying in the
 * dark for a while; it is not a fresh set of cells.
 */
const FOUND_CHARGE = TUNING.flashlight.batteryCharge;

export interface Flashlight {
  /** Whether the beam burns charge on this level. See `setDrains`. */
  setDrains(drains: boolean): void;
  /** True once the player has picked one up. */
  readonly has: boolean;

  /** Burn charge while lit and switch off when it dies. */
  update(dt: number, state: PlayerState): void;

  /** Player pressed the key. Does nothing useful without a flashlight or charge. */
  toggle(state: PlayerState): void;

  /** Back to the start-of-run state: no flashlight, no charge, off. */
  reset(state: PlayerState): void;

  /**
   * Put a saved torch back. Ownership, absolute charge and the switch, all at
   * once and without an event — this is a run being reconstituted, not a torch
   * being found, and every other door into this state (`item:pickup`,
   * `flashlight:recharge`) is a door with a sound attached and arithmetic on
   * the other side of it.
   */
  restore(state: PlayerState, charge: number, on: boolean): void;

  dispose(): void;
}

class Light implements Flashlight {
  /** False on levels that hand the torch out for free. */
  private drains = true;

  setDrains(drains: boolean): void {
    this.drains = drains;
  }

  has = false;

  private nagTimer = 0;
  private readonly unsubscribe: Array<() => void> = [];

  constructor(private readonly events: EventBus) {
    this.unsubscribe.push(
      events.on('item:pickup', ({ kind }) => {
        if (kind !== 'flashlight') return;
        this.has = true;
        this.pendingCharge = Math.max(this.pendingCharge, FOUND_CHARGE);
      })
    );
    this.unsubscribe.push(
      events.on('flashlight:recharge', ({ amount }) => {
        this.pendingCharge += amount;
      })
    );
  }

  /**
   * Charge granted by events between frames. Buffered rather than written
   * straight into `PlayerState` because the controller owns that object and an
   * event can arrive at any point in the frame.
   */
  private pendingCharge = 0;

  update(dt: number, state: PlayerState): void {
    if (this.pendingCharge > 0) {
      state.flashlightCharge = clamp(state.flashlightCharge + this.pendingCharge, 0, 100);
      this.pendingCharge = 0;
    }
    if (this.nagTimer > 0) this.nagTimer -= dt;

    if (!state.flashlightOn) return;
    if (!this.has || state.flashlightCharge <= 0) {
      state.flashlightOn = false;
      return;
    }

    // Some levels hand the torch out for free. Level 6 is pitch dark by design,
    // and on a floor where the light *is* the level, metering it turns the whole
    // place into an inventory problem rather than a dark one.
    if (!this.drains) return;

    const drain = (100 / TUNING.flashlight.fullDuration) * dt;
    state.flashlightCharge = Math.max(0, state.flashlightCharge - drain);
    if (state.flashlightCharge <= 0) {
      state.flashlightOn = false;
      this.events.emit('flashlight:toggle', { on: false });
      this.events.emit('flashlight:depleted', {});
    }
  }

  toggle(state: PlayerState): void {
    if (!this.has) {
      if (this.nagTimer <= 0) {
        this.nagTimer = NAG_INTERVAL;
        this.events.emit('ui:toast', { text: 'You have nothing to light the way.', tone: 'info' });
      }
      return;
    }
    if (!state.flashlightOn && state.flashlightCharge <= 0) {
      // Still emit: a dead switch clicking twice is the point.
      this.events.emit('flashlight:toggle', { on: false });
      return;
    }
    state.flashlightOn = !state.flashlightOn;
    this.events.emit('flashlight:toggle', { on: state.flashlightOn });
  }

  reset(state: PlayerState): void {
    this.has = false;
    this.pendingCharge = 0;
    this.nagTimer = 0;
    state.flashlightOn = false;
    state.flashlightCharge = 0;
  }

  restore(state: PlayerState, charge: number, on: boolean): void {
    this.has = true;
    // Anything buffered belongs to the run that is being replaced.
    this.pendingCharge = 0;
    this.nagTimer = 0;
    state.flashlightCharge = clamp(charge, 0, 100);
    state.flashlightOn = on && state.flashlightCharge > 0;
  }

  dispose(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
  }
}

export function makeFlashlight(events: EventBus): Flashlight {
  return new Light(events);
}
